/**
 * Admin Marketplace Activity Page
 *
 * Three-tab view showing:
 * 1. Overview - Stats cards and marketplace breakdown
 * 2. Brokered Transactions - Filtered paginated list
 * 3. Broker Service - xrp.deals broker executions, pending offers, revenue
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';

// ==================== API ====================

const API_BASE = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'http://localhost:3703/api/v1';

async function fetchWithAuth(endpoint: string) {
  const token = localStorage.getItem('xrpl_marketplace_token');
  const res = await fetch(`${API_BASE}${endpoint}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ==================== Types ====================

interface OverviewStats {
  overview: {
    brokeredCount: number;
    brokeredVolume: number;
    p2pCount: number;
    p2pVolume: number;
    brokered24hCount: number;
    brokered24hVolume: number;
  };
  marketplaces: Array<{
    marketplace: string;
    count: number;
    totalVolume: number;
    avgPrice: number;
    count24h: number;
    volume24h: number;
  }>;
  brokerService: {
    totalExecutions: number;
    successCount: number;
    failedCount: number;
    totalFeeXrp: number;
    pendingOffers: number;
  };
}

interface BrokeredTransaction {
  hash: string;
  marketplace: string;
  fromAddress: string;
  toAddress: string;
  nftId: string;
  amount: number;
  collectionId: string | null;
  collectionName: string | null;
  collectionSlug: string | null;
  occurredAt: string;
}

interface BrokerExecution {
  id: number;
  sellOfferId: string;
  buyOfferId: string;
  nftTokenId: string;
  sellerAddress: string;
  buyerAddress: string;
  sellAmountDrops: number;
  buyAmountDrops: number;
  brokerFeeDrops: number;
  status: string;
  transactionHash: string | null;
  errorMessage: string | null;
  createdAt: string;
  executedAt: string | null;
  collectionName: string | null;
  collectionSlug: string | null;
}

interface PendingOffer {
  id: number;
  offerId: string;
  nftTokenId: string;
  offerType: string;
  ownerAddress: string;
  amountDrops: number;
  destination: string;
  expiration: string | null;
  createdAt: string;
  ledgerIndex: number;
}

interface DailyRevenue {
  date: string;
  trades: number;
  totalFeeXrp: number;
  totalVolumeXrp: number;
  avgFeeXrp: number;
}

interface BrokerServiceData {
  stats: {
    totalExecutions: number;
    successCount: number;
    failedCount: number;
    successRate: string;
    totalRevenueXrp: number;
    pendingOffers: number;
  };
  executions: BrokerExecution[];
  executionsPagination: { total: number; page: number; limit: number; totalPages: number };
  pendingOffers: PendingOffer[];
  pendingOffersTotal: number;
  dailyRevenue: DailyRevenue[];
}

type TabKey = 'overview' | 'transactions' | 'broker';

// ==================== Helpers ====================

function formatXrp(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || amount === 0) return '0 XRP';
  return `${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} XRP`;
}

function formatXrpCompact(amount: number): string {
  if (amount >= 1_000_000) return `${(amount / 1_000_000).toFixed(1)}M XRP`;
  if (amount >= 1_000) return `${(amount / 1_000).toFixed(1)}K XRP`;
  return `${amount.toLocaleString(undefined, { maximumFractionDigits: 2 })} XRP`;
}

function formatDropsToXrp(drops: number): string {
  const xrp = drops / 1_000_000;
  return `${xrp.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 6 })} XRP`;
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function truncateAddress(address: string): string {
  if (!address) return '-';
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

function truncateHash(hash: string): string {
  if (!hash) return '-';
  return `${hash.slice(0, 10)}...`;
}

function getMarketplaceColor(marketplace: string): string {
  switch (marketplace) {
    case 'xrp.deals': return 'bg-blue-100 text-blue-700';
    case 'xrp.cafe': return 'bg-orange-100 text-orange-700';
    case 'bidds.com': return 'bg-green-100 text-green-700';
    case 'opmarket.ai': return 'bg-purple-100 text-purple-700';
    default: return 'bg-gray-100 text-gray-700';
  }
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'success': return 'bg-green-100 text-green-700';
    case 'failed': return 'bg-red-100 text-red-700';
    case 'pending': return 'bg-yellow-100 text-yellow-700';
    default: return 'bg-gray-100 text-gray-600';
  }
}

// ==================== Overview Tab ====================

function OverviewTab({ stats }: { stats: OverviewStats | null }) {
  if (!stats) return null;

  const { overview, marketplaces, brokerService } = stats;
  const totalSales = overview.brokeredCount + overview.p2pCount;
  const brokeredPct = totalSales > 0 ? ((overview.brokeredCount / totalSales) * 100).toFixed(1) : '0';

  // Find max count for bar visualization
  const maxCount = Math.max(...marketplaces.map((m) => m.count), 1);

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-4">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 shadow-sm">
          <div className="text-sm font-medium text-blue-600">Brokered Sales</div>
          <div className="mt-1 text-2xl font-bold text-blue-700">{overview.brokeredCount.toLocaleString()}</div>
          <div className="mt-0.5 text-xs text-blue-500">{brokeredPct}% of all sales</div>
        </div>
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 shadow-sm">
          <div className="text-sm font-medium text-green-600">Brokered Volume</div>
          <div className="mt-1 text-2xl font-bold text-green-700">{formatXrpCompact(overview.brokeredVolume)}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="text-sm font-medium text-gray-500">24h Brokered Sales</div>
          <div className="mt-1 text-2xl font-bold text-gray-900">{overview.brokered24hCount}</div>
          <div className="mt-0.5 text-xs text-gray-400">{formatXrpCompact(overview.brokered24hVolume)}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="text-sm font-medium text-gray-500">P2P Sales</div>
          <div className="mt-1 text-2xl font-bold text-gray-900">{overview.p2pCount.toLocaleString()}</div>
          <div className="mt-0.5 text-xs text-gray-400">{formatXrpCompact(overview.p2pVolume)}</div>
        </div>
      </div>

      {/* Marketplace Breakdown */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Marketplace Breakdown</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Marketplace</th>
                <th className="py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Sales</th>
                <th className="py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Volume</th>
                <th className="py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Avg Price</th>
                <th className="py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">24h Sales</th>
                <th className="py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">24h Volume</th>
                <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 w-48">Share</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {marketplaces.map((mp) => (
                <tr key={mp.marketplace}>
                  <td className="py-3">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getMarketplaceColor(mp.marketplace)}`}>
                      {mp.marketplace}
                    </span>
                  </td>
                  <td className="py-3 text-right text-sm text-gray-900 font-medium">{mp.count.toLocaleString()}</td>
                  <td className="py-3 text-right text-sm text-gray-900">{formatXrpCompact(mp.totalVolume)}</td>
                  <td className="py-3 text-right text-sm text-gray-600">{formatXrp(mp.avgPrice)}</td>
                  <td className="py-3 text-right text-sm text-gray-600">{mp.count24h}</td>
                  <td className="py-3 text-right text-sm text-gray-600">{formatXrpCompact(mp.volume24h)}</td>
                  <td className="py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-2 flex-1 rounded-full bg-gray-100">
                        <div
                          className="h-2 rounded-full bg-blue-500"
                          style={{ width: `${(mp.count / maxCount) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 w-10 text-right">
                        {((mp.count / overview.brokeredCount) * 100).toFixed(0)}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Broker Service Summary */}
      <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-6 shadow-sm">
        <h3 className="text-sm font-semibold text-indigo-700 mb-4">xrp.deals Broker Service</h3>
        <div className="grid gap-4 sm:grid-cols-4">
          <div>
            <div className="text-xs text-indigo-500">Executions</div>
            <div className="text-lg font-bold text-indigo-700">{brokerService.totalExecutions}</div>
            <div className="text-xs text-indigo-400">
              {brokerService.successCount} success / {brokerService.failedCount} failed
            </div>
          </div>
          <div>
            <div className="text-xs text-indigo-500">Revenue</div>
            <div className="text-lg font-bold text-indigo-700">{formatXrp(brokerService.totalFeeXrp)}</div>
          </div>
          <div>
            <div className="text-xs text-indigo-500">Success Rate</div>
            <div className="text-lg font-bold text-indigo-700">
              {brokerService.totalExecutions > 0
                ? ((brokerService.successCount / brokerService.totalExecutions) * 100).toFixed(1)
                : '0'}%
            </div>
          </div>
          <div>
            <div className="text-xs text-indigo-500">Pending Offers</div>
            <div className="text-lg font-bold text-indigo-700">{brokerService.pendingOffers}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== Transactions Tab ====================

function TransactionsTab() {
  const [transactions, setTransactions] = useState<BrokeredTransaction[]>([]);
  const [pagination, setPagination] = useState<{ total: number; page: number; limit: number; totalPages: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [marketplace, setMarketplace] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);

  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '50');
      if (marketplace) params.set('marketplace', marketplace);
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);

      const result = await fetchWithAuth(`/admin/marketplace-activity/transactions?${params.toString()}`);
      if (result.success) {
        setTransactions(result.data.transactions);
        setPagination(result.data.pagination);
      } else {
        throw new Error(result.error || 'Failed to load transactions');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, marketplace, fromDate, toDate]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const handleFilterChange = () => {
    setPage(1);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Marketplace</label>
          <select
            value={marketplace}
            onChange={(e) => { setMarketplace(e.target.value); handleFilterChange(); }}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">All Marketplaces</option>
            <option value="xrp.deals">xrp.deals</option>
            <option value="xrp.cafe">xrp.cafe</option>
            <option value="bidds.com">bidds.com</option>
            <option value="opmarket.ai">opmarket.ai</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); handleFilterChange(); }}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); handleFilterChange(); }}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        {(marketplace || fromDate || toDate) && (
          <button
            onClick={() => { setMarketplace(''); setFromDate(''); setToDate(''); handleFilterChange(); }}
            className="rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-200"
          >
            Clear
          </button>
        )}
      </div>

      {error && <ErrorDisplay error={error} onRetry={loadTransactions} />}

      {loading && transactions.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent" />
            <p className="mt-4 text-sm text-gray-600">Loading transactions...</p>
          </div>
        </div>
      )}

      {!loading && transactions.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
          <p className="text-gray-500">No brokered transactions found for the selected filters.</p>
        </div>
      )}

      {transactions.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">TX Hash</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Marketplace</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Seller</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Buyer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">NFT</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Collection</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {transactions.map((tx) => (
                  <tr key={tx.hash} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <a
                        href={`https://bithomp.com/en/tx/${encodeURIComponent(tx.hash)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-mono text-blue-600 hover:text-blue-800"
                      >
                        {truncateHash(tx.hash)}
                      </a>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getMarketplaceColor(tx.marketplace)}`}>
                        {tx.marketplace}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-600">
                      {truncateAddress(tx.fromAddress)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-600">
                      {truncateAddress(tx.toAddress)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-500">
                      {tx.nftId ? truncateHash(tx.nftId) : '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                      {formatXrp(tx.amount)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 truncate max-w-[150px]">
                      {tx.collectionName || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(tx.occurredAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-3">
              <div className="text-sm text-gray-700">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} transactions
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(1)}
                  disabled={pagination.page <= 1}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  First
                </button>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page <= 1}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={pagination.page >= pagination.totalPages}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
                <button
                  onClick={() => setPage(pagination.totalPages)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== Broker Service Tab ====================

function BrokerServiceTab() {
  const [data, setData] = useState<BrokerServiceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '25');

      const result = await fetchWithAuth(`/admin/marketplace-activity/broker-service?${params.toString()}`);
      if (result.success) {
        setData(result.data);
      } else {
        throw new Error(result.error || 'Failed to load broker service data');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent" />
          <p className="mt-4 text-sm text-gray-600">Loading broker service data...</p>
        </div>
      </div>
    );
  }

  if (error) return <ErrorDisplay error={error} onRetry={loadData} />;
  if (!data) return null;

  const { stats, executions, executionsPagination, pendingOffers, pendingOffersTotal, dailyRevenue } = data;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-4">
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 shadow-sm">
          <div className="text-sm font-medium text-indigo-600">Total Executions</div>
          <div className="mt-1 text-2xl font-bold text-indigo-700">{stats.totalExecutions}</div>
        </div>
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 shadow-sm">
          <div className="text-sm font-medium text-green-600">Success Rate</div>
          <div className="mt-1 text-2xl font-bold text-green-700">{stats.successRate}%</div>
          <div className="mt-0.5 text-xs text-green-500">
            {stats.successCount} success / {stats.failedCount} failed
          </div>
        </div>
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 shadow-sm">
          <div className="text-sm font-medium text-blue-600">Total Revenue</div>
          <div className="mt-1 text-2xl font-bold text-blue-700">{formatXrp(stats.totalRevenueXrp)}</div>
        </div>
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 shadow-sm">
          <div className="text-sm font-medium text-yellow-600">Pending Offers</div>
          <div className="mt-1 text-2xl font-bold text-yellow-700">{stats.pendingOffers}</div>
        </div>
      </div>

      {/* Daily Revenue */}
      {dailyRevenue.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Daily Revenue (Last 30 Days)</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Date</th>
                  <th className="py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Trades</th>
                  <th className="py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Fee Revenue</th>
                  <th className="py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Volume</th>
                  <th className="py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Avg Fee</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {dailyRevenue.map((d) => (
                  <tr key={d.date}>
                    <td className="py-2 text-sm text-gray-900">{formatDate(d.date)}</td>
                    <td className="py-2 text-right text-sm text-gray-900 font-medium">{d.trades}</td>
                    <td className="py-2 text-right text-sm text-green-700 font-medium">{formatXrp(d.totalFeeXrp)}</td>
                    <td className="py-2 text-right text-sm text-gray-600">{formatXrp(d.totalVolumeXrp)}</td>
                    <td className="py-2 text-right text-sm text-gray-500">{formatXrp(d.avgFeeXrp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Executions */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-sm font-semibold text-gray-700">Recent Executions</h3>
        </div>
        {executions.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">No broker executions found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Collection</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">NFT</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Seller</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Buyer</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Sell</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Buy</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Fee</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">TX</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {executions.map((exec) => (
                  <tr key={exec.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">#{exec.id}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {exec.collectionSlug ? (
                        <Link to={`/collections/${exec.collectionSlug}`} className="text-blue-600 hover:text-blue-800">
                          {exec.collectionName}
                        </Link>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-mono">
                      <Link to={`/nfts/${exec.nftTokenId}`} className="text-blue-600 hover:text-blue-800">
                        {truncateHash(exec.nftTokenId)}
                      </Link>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-mono">
                      <Link to={`/wallets/${exec.sellerAddress}`} className="text-blue-600 hover:text-blue-800">
                        {truncateAddress(exec.sellerAddress)}
                      </Link>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-mono">
                      <Link to={`/wallets/${exec.buyerAddress}`} className="text-blue-600 hover:text-blue-800">
                        {truncateAddress(exec.buyerAddress)}
                      </Link>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatDropsToXrp(exec.sellAmountDrops)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatDropsToXrp(exec.buyAmountDrops)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium text-green-700">
                      {formatDropsToXrp(exec.brokerFeeDrops)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(exec.status)}`}>
                        {exec.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {exec.transactionHash ? (
                        <a
                          href={`https://bithomp.com/en/tx/${encodeURIComponent(exec.transactionHash)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-mono text-blue-600 hover:text-blue-800"
                        >
                          {truncateHash(exec.transactionHash)}
                        </a>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(exec.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Executions Pagination */}
        {executionsPagination && executionsPagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-3">
            <div className="text-sm text-gray-700">
              Page {executionsPagination.page} of {executionsPagination.totalPages} ({executionsPagination.total} total)
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={executionsPagination.page <= 1}
                className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(executionsPagination.totalPages, p + 1))}
                disabled={executionsPagination.page >= executionsPagination.totalPages}
                className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Pending Offers */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-sm font-semibold text-gray-700">
            Pending Offers
            <span className="ml-2 rounded-full bg-yellow-100 px-2 py-0.5 text-xs text-yellow-700">
              {pendingOffersTotal}
            </span>
          </h3>
        </div>
        {pendingOffers.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">No pending broker offers.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Offer ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">NFT</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Owner</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Expires</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {pendingOffers.map((offer) => (
                  <tr key={offer.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-600">
                      {truncateHash(offer.offerId)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-600">
                      {truncateHash(offer.nftTokenId)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                        offer.offerType === 'buy' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {offer.offerType}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-600">
                      {truncateAddress(offer.ownerAddress)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                      {formatDropsToXrp(offer.amountDrops)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(offer.createdAt)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {offer.expiration ? formatDateTime(offer.expiration) : 'Never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== Main Component ====================

export function AdminMarketplaceActivityPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      setStatsError(null);
      const result = await fetchWithAuth('/admin/marketplace-activity/stats');
      if (result.success) {
        setStats(result.data);
      } else {
        throw new Error(result.error || 'Failed to load stats');
      }
    } catch (err: any) {
      setStatsError(err.message);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'transactions', label: 'Brokered Transactions' },
    { key: 'broker', label: 'Broker Service' },
  ];

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Marketplace Activity</h1>
            <p className="mt-2 text-sm text-gray-600">
              Brokered sale activity across all marketplaces and our broker matching service
            </p>
          </div>
          <button
            onClick={loadStats}
            disabled={statsLoading}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {statsLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex gap-4">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`pb-3 text-sm font-medium transition-colors border-b-2 ${
                  activeTab === tab.key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {statsError && activeTab === 'overview' && (
          <ErrorDisplay error={statsError} onRetry={loadStats} className="mb-6" />
        )}

        {statsLoading && activeTab === 'overview' && !stats && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent" />
              <p className="mt-4 text-sm text-gray-600">Loading marketplace stats...</p>
            </div>
          </div>
        )}

        {activeTab === 'overview' && <OverviewTab stats={stats} />}
        {activeTab === 'transactions' && <TransactionsTab />}
        {activeTab === 'broker' && <BrokerServiceTab />}
      </div>
    </AdminLayout>
  );
}
